import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Pagination,
  Tooltip,
  Snackbar,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  AdminPanelSettings as AdminIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import {
  UserContextRoleService,
  UserContextRole,
} from "../../services/UserContextRoleService";
import { UserService, User } from "../../services/UserService";
import { createAxiosInstance } from "../../services/AxiosService";
import { isAdmin } from "../../services/AuthService";

const axiosInstance = createAxiosInstance();

interface Role {
  id: string;
  name: string;
  description?: string;
}

interface RoleContext {
  id: string;
  name: string;
  description?: string;
}

interface CreateUserContextRoleData {
  user_id: string;
  role_id: string;
  role_context_id: string;
}

const RoleManagement: React.FC = () => {
  const navigate = useNavigate();
  const [userContextRoles, setUserContextRoles] = useState<UserContextRole[]>(
    [],
  );
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [contexts, setContexts] = useState<RoleContext[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRoles, setTotalRoles] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [viewDialogOpen, setViewDialogOpen] = useState<boolean>(false);
  const [selectedUserContextRole, setSelectedUserContextRole] =
    useState<UserContextRole | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success",
  );
  const [tabValue, setTabValue] = useState<number>(0);

  // Form state for create
  const [formData, setFormData] = useState<CreateUserContextRoleData>({
    user_id: "",
    role_id: "",
    role_context_id: "",
  });

  const [hasAdminAccess] = useState<boolean>(isAdmin());

  const fetchUserContextRoles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await UserContextRoleService.getAllUserContextRoles({
        page: currentPage,
        per_page: 10,
        search: searchTerm || undefined,
      });
      setUserContextRoles(response.user_context_roles || []);
      setTotalRoles(response.pagination?.total || 0);
    } catch (error) {
      console.error("Error fetching user context roles:", error);
      showSnackbar("Failed to fetch user context roles", "error");
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await UserService.getUsers({
        page: 1,
        per_page: 100, // Get more users for the dropdown
      });
      setUsers(response.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, []);

  const fetchRolesAndContexts = useCallback(async () => {
    try {
      const [rolesRes, contextsRes] = await Promise.all([
        axiosInstance.get("/generic/roles", { params: { per_page: 100 } }),
        axiosInstance.get("/generic/role_contexts", {
          params: { per_page: 100 },
        }),
      ]);
      console.log("Roles response:", rolesRes.data);
      console.log("Contexts response:", contextsRes.data);
      setRoles(rolesRes.data?.roles || []);
      setContexts(contextsRes.data?.role_contexts || []);
    } catch (error) {
      console.error("Error fetching roles and contexts:", error);
      showSnackbar("Failed to fetch roles and contexts", "error");
    }
  }, []);

  useEffect(() => {
    if (isAdmin()) {
      fetchUserContextRoles();
      fetchUsers();
      fetchRolesAndContexts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUserContextRoles]);

  const handleCreateUserContextRole = async () => {
    try {
      await UserContextRoleService.createUserContextRole(formData);
      setCreateDialogOpen(false);
      resetForm();
      fetchUserContextRoles();
      showSnackbar("User context role created successfully", "success");
    } catch (error) {
      console.error("Error creating user context role:", error);
      showSnackbar("Failed to create user context role", "error");
    }
  };

  const handleDeleteUserContextRole = async () => {
    if (!selectedUserContextRole) return;

    try {
      await UserContextRoleService.deleteUserContextRole(
        selectedUserContextRole.id,
      );
      setDeleteDialogOpen(false);
      setSelectedUserContextRole(null);
      fetchUserContextRoles();
      showSnackbar("User context role deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting user context role:", error);
      showSnackbar("Failed to delete user context role", "error");
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setCreateDialogOpen(true);
  };

  const openDeleteDialog = (userContextRole: UserContextRole) => {
    setSelectedUserContextRole(userContextRole);
    setDeleteDialogOpen(true);
  };

  const openViewDialog = (userContextRole: UserContextRole) => {
    setSelectedUserContextRole(userContextRole);
    setViewDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      user_id: "",
      role_id: "",
      role_context_id: "",
    });
  };

  const showSnackbar = (message: string, severity: "success" | "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const getRoleChip = (role: any, context: any) => {
    // Handle null/undefined values
    const roleName = role?.name || "Unknown Role";
    const contextName = context?.name || "Unknown Context";
    const isAdminRole = roleName === "admin";
    return (
      <Chip
        label={`${roleName}:${contextName}`}
        size="small"
        variant="outlined"
        icon={isAdminRole ? <AdminIcon /> : <PersonIcon />}
      />
    );
  };

  const getUserFullName = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return "Unknown User";
    if (user.first_name || user.last_name) {
      return `${user.first_name || ""} ${user.last_name || ""}`.trim();
    }
    return user.username;
  };

  const groupedByUser = userContextRoles.reduce(
    (acc, ucr) => {
      const userId = ucr.user_id;
      if (!acc[userId]) {
        acc[userId] = {
          userId: userId,
          roles: [],
        };
      }
      acc[userId].roles.push(ucr);
      return acc;
    },
    {} as Record<string, { userId: string; roles: UserContextRole[] }>,
  );

  const groupedByRole = userContextRoles.reduce(
    (acc, ucr) => {
      // Safely handle cases where role or context might be undefined
      if (!ucr.role || !ucr.context) {
        console.warn("Invalid role or context data:", ucr);
        return acc;
      }

      const roleKey = `${ucr.role.name}:${ucr.context.name}`;
      if (!acc[roleKey]) {
        acc[roleKey] = {
          role: ucr.role,
          context: ucr.context,
          users: [],
        };
      }
      acc[roleKey].users.push(ucr);
      return acc;
    },
    {} as Record<string, { role: any; context: any; users: UserContextRole[] }>,
  );

  if (!hasAdminAccess) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          Access Denied: You don't have administrator privileges.
        </Alert>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{ mt: 4, mb: 4 }}
      data-testid="role-management"
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/admin?tab=0")}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>
          <Typography variant="h4" component="h1">
            Role & Context Management
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
        >
          Assign Role
        </Button>
      </Box>

      {/* Search and Filters */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 3,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 0,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search Roles"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by context or role name..."
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchUserContextRoles}
              disabled={loading}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs for different views */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 0,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          aria-label="role management tabs"
        >
          <Tab label="All Assignments" />
          <Tab label="By User" />
          <Tab label="By Role" />
        </Tabs>
      </Paper>

      {/* Content based on tab */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 0,
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {tabValue === 0 && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Role & Context</TableCell>
                      <TableCell>Assigned</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userContextRoles.map((ucr) => (
                      <TableRow key={ucr.id}>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <PersonIcon
                              sx={{ mr: 1, color: "text.secondary" }}
                            />
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {getUserFullName(ucr.user_id)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                @
                                {users.find((u) => u.id === ucr.user_id)
                                  ?.username || "unknown"}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {getRoleChip(ucr.role, ucr.context)}
                        </TableCell>
                        <TableCell>
                          {new Date(ucr.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => openViewDialog(ucr)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove Assignment">
                            <IconButton
                              size="small"
                              onClick={() => openDeleteDialog(ucr)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {tabValue === 1 && (
              <Grid container spacing={2}>
                {Object.entries(groupedByUser).map(
                  ([userId, { userId: userIdValue, roles }]) => (
                    <Grid item xs={12} md={6} key={userId}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 0,
                        }}
                      >
                        <Box sx={{ p: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <PersonIcon
                              sx={{ mr: 1, color: "text.secondary" }}
                            />
                            <Box>
                              <Typography variant="h6">
                                {getUserFullName(userIdValue)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                @
                                {users.find((u) => u.id === userIdValue)
                                  ?.username || "unknown"}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Assigned Roles ({roles.length}):
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                            }}
                          >
                            {roles.map((role) => (
                              <Box key={role.id}>
                                {getRoleChip(role.role, role.context)}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ),
                )}
              </Grid>
            )}

            {tabValue === 2 && (
              <Grid container spacing={2}>
                {Object.entries(groupedByRole).map(
                  ([roleKey, { role, context, users }]) => (
                    <Grid item xs={12} md={6} key={roleKey}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 0,
                        }}
                      >
                        <Box sx={{ p: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <SecurityIcon
                              sx={{ mr: 1, color: "text.secondary" }}
                            />
                            <Box>{getRoleChip(role, context)}</Box>
                          </Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Assigned Users ({users.length}):
                          </Typography>
                          <Box>
                            {users.map((userRole) => (
                              <Chip
                                key={userRole.id}
                                label={`@${getUserFullName(userRole.user_id)}`}
                                size="small"
                                variant="outlined"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ),
                )}
              </Grid>
            )}

            {/* Pagination */}
            {tabValue === 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={Math.ceil(totalRoles / 10)}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Create Role Assignment Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Role to User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>User</InputLabel>
                <Select
                  value={formData.user_id}
                  onChange={(e) =>
                    setFormData({ ...formData, user_id: e.target.value })
                  }
                  label="User"
                >
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {getUserFullName(user.id)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role_id}
                  onChange={(e) =>
                    setFormData({ ...formData, role_id: e.target.value })
                  }
                  label="Role"
                >
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Context</InputLabel>
                <Select
                  value={formData.role_context_id}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      role_context_id: e.target.value,
                    })
                  }
                  label="Context"
                >
                  {contexts.map((context) => (
                    <MenuItem key={context.id} value={context.id}>
                      {context.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateUserContextRole} variant="contained">
            Assign Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Remove Role Assignment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove the role assignment "
            {selectedUserContextRole?.role?.name || "Unknown Role"}:
            {selectedUserContextRole?.context?.name || "Unknown Context"}" from
            user "
            {
              users.find((u) => u.id === selectedUserContextRole?.user_id)
                ?.username
            }
            "? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteUserContextRole} variant="outlined">
            Remove Assignment
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Role Assignment Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Role Assignment Details</DialogTitle>
        <DialogContent>
          {selectedUserContextRole && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">User</Typography>
                <Typography>
                  {getUserFullName(selectedUserContextRole.user_id)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  @
                  {users.find((u) => u.id === selectedUserContextRole.user_id)
                    ?.username || "unknown"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Role</Typography>
                <Typography>
                  {selectedUserContextRole.role?.name || "Unknown Role"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Context</Typography>
                <Typography>
                  {selectedUserContextRole.context?.name || "Unknown Context"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Permission String</Typography>
                <Chip
                  label={`${selectedUserContextRole.role?.name || "Unknown Role"}:${selectedUserContextRole.context?.name || "Unknown Context"}`}
                  color={
                    selectedUserContextRole.role?.name === "admin"
                      ? "error"
                      : "primary"
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Assigned</Typography>
                <Typography>
                  {new Date(
                    selectedUserContextRole.created_at,
                  ).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Assignment ID</Typography>
                <Typography>{selectedUserContextRole.id}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default RoleManagement;
